import { useState } from 'react'
import { authApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

function errorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    fallback
  )
}

export default function SettingsPage() {
  const [step, setStep] = useState<'current' | 'new'>('current')
  const [currentPassword, setCurrentPassword] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { updateRecruiter } = useAuth()

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)
    try {
      await authApi.verifyPassword(currentPassword)
      setStep('new')
    } catch (err: unknown) {
      setError(errorMessage(err, 'הסיסמה הנוכחית שגויה'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (password !== confirmPassword) {
      setError('הסיסמאות אינן תואמות')
      return
    }
    if (password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים')
      return
    }
    setLoading(true)
    try {
      const response = await authApi.changePassword(password, currentPassword)
      updateRecruiter(response.recruiter)
      setCurrentPassword('')
      setPassword('')
      setConfirmPassword('')
      setStep('current')
      setSuccess('הסיסמה עודכנה')
    } catch (err: unknown) {
      setError(errorMessage(err, 'שגיאה בעדכון הסיסמה'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>הגדרות</CardTitle>
          <CardDescription>
            {step === 'current' ? 'אשר את הסיסמה הנוכחית' : 'בחר סיסמה חדשה'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
              {success}
            </div>
          )}
          {step === 'current' ? (
            <form onSubmit={handleVerify} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="current-password">סיסמה נוכחית</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? 'בודק...' : 'המשך'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="new-password">סיסמה חדשה</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirm-password">אימות סיסמה</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? 'שומר...' : 'עדכן סיסמה'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
